import { css } from "@compiled/react";
import { graphql, useFragment } from "react-relay";

import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import { collectionHeaderSectionRelayFragment$key } from "@/__generated__/collectionHeaderSectionRelayFragment.graphql";

interface CollectionHeaderSectionProps {
    collection: collectionHeaderSectionRelayFragment$key;
}

const backgroundStyles = css({
    paddingBlockStart: 64,
    background:
        "url('https://wac-cdn.atlassian.com/misc-assets/webp-images/confluence/templates/collection_bg.webp') center/cover no-repeat",

    [media.below.md]: {
        paddingBlockStart: 0,
        backgroundImage: "none"
    }
});

const collectionHeadingSection = css({
    "textAlign": "center",
    "maxWidth": 1045,
    "paddingBlockStart": 80,
    "paddingInlineStart": 80,
    "paddingInlineEnd": 80,
    "paddingBlockEnd": 63,
    "margin": "0 auto",
    "backgroundColor": "#ffffff",

    [media.below.md]: {
        paddingBlockEnd: 0
    },
    "@media (min-width: 30rem) and (max-width: 67.49rem)": {
        paddingInlineStart: 64,
        paddingInlineEnd: 64
    },
    [media.below.xs]: {
        paddingInlineStart: 32,
        paddingInlineEnd: 32
    }
});

const collectionTitle = css({
    fontSize: "3rem",
    lineHeight: "3.5rem",
    fontWeight: 500,
    // FIXME: GWP-3063: Disabling instead of fixing so we don't deviate from the control visually.
    // eslint-disable-next-line local/ensure-wac-token-usage
    color: "#172b4d",
    // FIXME: GWP-3063: Disabling instead of fixing so we don't deviate from the control visually.
    // eslint-disable-next-line local/ensure-wac-token-usage
    fontFamily: "var(--bxp-charlie-display-font-family)",
    margin: 0,
    marginBlockEnd: "1rem",

    [media.only.md]: {
        fontSize: "2.75rem",
        lineHeight: "3.125rem"
    },
    [media.below.md]: {
        fontSize: "2.5rem",
        lineHeight: "2.875rem"
    }
});

const collectionHeadline = css({
    "fontSize": "1.25rem",
    "lineHeight": "1.75rem",
    "fontWeight": 400,
    "color": "#172b4d",
    "fontFamily": "var(--bxp-charlie-display-font-family)",
    "margin": 0,
    "marginBlockEnd": `${token("space.300")}`,

    "@media (max-width: 39.99rem)": {
        fontSize: "1.25rem",
        lineHeight: "1.75rem"
    },

    [media.below.md]: {
        marginBlockEnd: `${token("space.500")}`
    }
});

const dividerStyle = css({
    display: "none",
    width: 140,
    // FIXME: GWP-3063: Disabling instead of fixing so we don't deviate from the control visually.
    // eslint-disable-next-line local/ensure-wac-token-usage
    border: "2px solid #0052cc",
    margin: "0 auto",

    [media.below.md]: {
        display: "block"
    }
});

const CollectionHeaderSectionRelayFragment = graphql`
    fragment collectionHeaderSectionRelayFragment on ContentPlatformTemplateCollection {
        name
        oneLinerHeadline
    }
`;

export const CollectionHeaderSection = ({
    collection
}: CollectionHeaderSectionProps) => {
    const { name, oneLinerHeadline } = useFragment(
        CollectionHeaderSectionRelayFragment,
        collection
    );
    return (
        <header css={backgroundStyles}>
            <div css={collectionHeadingSection}>
                <h1 css={collectionTitle}>{name}</h1>
                <p css={collectionHeadline}>{oneLinerHeadline}</p>
                <hr css={dividerStyle} />
            </div>
        </header>
    );
};
